import React, { useState, useEffect } from 'react';
import { Calendar, User, ArrowRight, Search, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNews } from '../hooks/useNews';
import { NewsArticle } from '../services/newsService';
import NewsDetail from './NewsDetail';

const NewsSection: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNews, setFilteredNews] = useState<NewsArticle[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [screenSize, setScreenSize] = useState('lg');
  
  const {
    news,
    loading,
    error,
    hasMore,
    searchNews,
    loadMore,
    refresh,
    clearError
  } = useNews({
    initialLimit: 6,
    autoFetch: true
  });

  // Handle search functionality
  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    setIsSearching(true);
    
    try {
      if (term.trim() === '') {
        setFilteredNews(news);
      } else {
        await searchNews(term.trim());
        setFilteredNews(news);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    setFilteredNews(news);
  }, [news]);

  // Handle screen size detection
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setScreenSize('sm');
      } else if (window.innerWidth < 1024) {
        setScreenSize('md');
      } else {
        setScreenSize('lg');
      }
    };

    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset currentSlide when news, filteredNews, or screen size changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [news, filteredNews, screenSize]);

  // Helper function to get cards per view based on screen size
  const getCardsPerView = () => {
    switch (screenSize) {
      case 'sm': return 1;
      case 'md': return 2;
      case 'lg': return 3;
      default: return 3;
    }
  };

  // Helper function to get max slide index
  const getMaxSlide = () => {
    const cardsPerView = getCardsPerView();
    return Math.max(0, filteredNews.length - cardsPerView);
  };

  // Helper function to get transform percentage
  const getTransformPercentage = () => {
    switch (screenSize) {
      case 'sm': return 100;
      case 'md': return 50;
      case 'lg': return 33.333;
      default: return 33.333;
    }
  };

  // Helper function to get card width
  const getCardWidth = () => {
    switch (screenSize) {
      case 'sm': return 'calc(100% - 16px)';
      case 'md': return 'calc(50% - 12px)';
      case 'lg': return 'calc(33.333% - 21.33px)';
      default: return 'calc(33.333% - 21.33px)';
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== '') {
        handleSearch(searchTerm);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle refresh
  const handleRefresh = async () => {
    setSearchTerm('');
    await refresh();
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="animate-pulse">
              <div className="h-10 bg-gray-700 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-6 bg-gray-700 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-2xl shadow-lg p-6 border border-purple-400/20">
                  <div className="h-4 bg-gray-600 rounded w-1/4 mb-4"></div>
                  <div className="h-6 bg-gray-600 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-600 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-600 rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-gray-600 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Error State */}
          <div className="text-center py-8">
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-300 mb-4">{error}</p>
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={clearError}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
                >
                  Dismiss
                </button>
                <button 
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (news.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Latest News & Updates
            </h2>
            <p className="text-gray-300 text-lg">
              Stay informed with the latest developments in technology and innovation
            </p>
          </div>
          
          {/* Search and Controls */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full sm:w-64"
                disabled={loading}
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                </div>
              )}
            </div>
            <button 
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        
        {/* News Slider */}
        <div className="relative">
          {/* Navigation Buttons */}
          {filteredNews.length > 1 && (
            <>
              <button
                onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                disabled={currentSlide === 0}
                className="absolute -left-4 md:-left-16 top-1/2 -translate-y-1/2 z-10 bg-purple-600/80 hover:bg-purple-600 disabled:bg-gray-600/50 disabled:cursor-not-allowed text-white p-2 md:p-3 rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
              >
                <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
              </button>
              <button
                onClick={() => setCurrentSlide(Math.min(getMaxSlide(), currentSlide + 1))}
                disabled={currentSlide >= getMaxSlide()}
                className="absolute -right-4 md:-right-16 top-1/2 -translate-y-1/2 z-10 bg-purple-600/80 hover:bg-purple-600 disabled:bg-gray-600/50 disabled:cursor-not-allowed text-white p-2 md:p-3 rounded-full transition-all duration-200 shadow-lg backdrop-blur-sm"
              >
                <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
              </button>
            </>
          )}
          
          {/* Slider Container */}
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out gap-4 md:gap-6 lg:gap-8"
              style={{ 
                transform: `translateX(-${currentSlide * getTransformPercentage()}%)` 
              }}
            >
              {filteredNews.map((article) => (
                <article
                  key={article.id}
                  onClick={() => setSelectedArticle(article)}
                  className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-4 md:p-6 transition-all duration-300 hover:border-purple-400/40 hover:shadow-2xl hover:shadow-purple-500/10 cursor-pointer relative overflow-hidden flex-shrink-0"
                  style={{ width: getCardWidth() }}
                >
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{article.formatted_date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                    <User className="w-4 h-4" />
                    <span>{article.author}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-300 mb-4 line-clamp-3 leading-relaxed">
                    {article.summary}
                  </p>
                  <div className="flex items-center text-purple-400 font-medium group-hover:text-purple-300 transition-colors duration-200">
                    <span className="mr-2">Read More</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                  
                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/5 to-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-indigo-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-sm"></div>
                </article>
              ))}
            </div>
          </div>
          
          {/* Slide Indicators */}
          {filteredNews.length > getCardsPerView() && (
            <div className="flex justify-center mt-8 gap-2">
              {Array.from({ length: getMaxSlide() + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    currentSlide === index 
                      ? 'bg-purple-500 scale-110' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Load More Button - Hidden in slider mode, but keep for potential future use */}
        {hasMore && filteredNews.length > 0 && false && (
          <div className="text-center mt-12">
            <button
              onClick={loadMore}
              disabled={loading}
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Loading...
                </>
              ) : (
                <>
                  Load More Articles
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        )}
        
        {/* Results Info */}
        {filteredNews.length > 0 && (
          <div className="text-center mt-8">
            <p className="text-gray-400 text-sm">
              Showing {filteredNews.length} article{filteredNews.length !== 1 ? 's' : ''}
              {searchTerm && ` for "${searchTerm}"`}
            </p>
          </div>
        )}
      </div>
      
      {/* News Detail Modal */}
      <NewsDetail 
        article={selectedArticle} 
        onClose={() => setSelectedArticle(null)} 
      />
    </section>
  );
};

export default NewsSection;